import { Badge, Tooltip } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren } from 'react';
import type { IconName } from '../../../components/ui/icon/types';
import { dateTimeFormatter } from '../../../utils/format-date';
import { formatMoneyNoSymbol, getCurrencySymbol } from '../../../utils/format-money';
import type { Discount, OtherRequirement, PartialDiscount } from '../types';

function getOtherRequirement(
  discount: PartialDiscount,
  type: string,
): OtherRequirement | undefined {
  return discount.other_requirements?.find((or) => or.requirement_type === type) as
    | OtherRequirement
    | undefined;
}

type BadgeTooltipWrapperProps = {
  tooltip: string;
  icon: IconName;
  enabled: boolean;
  inline?: boolean;
};

function BadgeTooltipWrapper({
  children,
  tooltip,
  icon,
  enabled,
  inline = false,
}: PropsWithChildren<BadgeTooltipWrapperProps>) {
  return (
    <Tooltip
      content={
        <div
          className={inline ? 'mb-1 flex flex-start gap-1' : 'mb-1 flex flex-start flex-col gap-2'}
        >
          <span className="text-spark-text-secondary">{tooltip}</span>
          {children}
        </div>
      }
    >
      <Badge interactive variant={enabled ? 'purple' : 'white'} icon={icon} iconSize="md" />
    </Tooltip>
  );
}

function isDefaultCustomerGroup(discount: Discount) {
  return discount.groups.length === 1 && discount.groups[0] === 'default';
}

function getSchedulingRequirement(discount: Discount) {
  return discount.start_date || discount.end_date
    ? {
        startDate: discount.start_date,
        endDate: discount.end_date,
      }
    : null;
}

function getSimultaneity(discount: Discount, discounts: Discount[]): Array<Discount> | null {
  if (discount.simultaneity === null) {
    return null;
  }

  return discounts.filter((d) => !!discount.simultaneity?.includes(d.id!));
}

function formattedStartDate(date: string | null): string {
  if (date === null) {
    return 'N/A';
  }

  return dateTimeFormatter.format(new Date(date));
}

type DiscountsRowProps = {
  discount: Discount;
  discounts: Discount[];
};

export function DiscountsRow({ discount, discounts }: DiscountsRowProps) {
  const couponRequirement = getOtherRequirement(discount, 'coupons');
  const groupRequirement = isDefaultCustomerGroup(discount) ? null : discount.groups;
  const schedulingRequirement = getSchedulingRequirement(discount);
  const spendAmountRequirement = getOtherRequirement(discount, 'spendAmount');
  const numItemsRequirement = getOtherRequirement(discount, 'numItems');
  const maxUsesRequirement = getOtherRequirement(discount, 'maxUsesPerCustomer');
  const excludedCustomersRequirement = discount.excluded_customers.length > 0;
  const productRequirement = discount.requirements.length > 0;
  const simultaneityRequirement = getSimultaneity(discount, discounts);
  const currencyRequirement = discount.currency_code;

  return (
    <div className="flex flex-wrap gap-1.5">
      <BadgeTooltipWrapper
        tooltip={t(
          couponRequirement
            ? 'features:discounts.components.list_table.tooltips.coupon-required'
            : 'features:discounts.components.list_table.tooltips.coupon-not-required',
        )}
        icon="couponRequirement"
        enabled={!!couponRequirement}
      >
        {couponRequirement && (
          <div className="flex flex-row flex-wrap gap-2">
            {couponRequirement.coupons.map((coupon) => (
              <Badge key={coupon.code} variant="green">
                {coupon.code}
              </Badge>
            ))}
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          groupRequirement
            ? 'features:discounts.components.list_table.tooltips.group-required'
            : 'features:discounts.components.list_table.tooltips.group-not-required',
        )}
        icon="userGroup"
        enabled={!!groupRequirement}
      >
        {groupRequirement && (
          <div className="flex flex-row flex-wrap gap-2">
            {groupRequirement.map((group) => (
              <Badge key={group} variant="green">
                {group}
              </Badge>
            ))}
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          schedulingRequirement
            ? 'features:discounts.components.list_table.tooltips.scheduling-required'
            : 'features:discounts.components.list_table.tooltips.scheduling-not-required',
        )}
        icon="calendar"
        enabled={!!schedulingRequirement}
      >
        {schedulingRequirement && (
          <div>
            {schedulingRequirement.startDate && (
              <span className="font-bold">
                {t('features:discounts.components.list_table.tooltips.from')}{' '}
                {formattedStartDate(schedulingRequirement.startDate)}
              </span>
            )}
            {schedulingRequirement.startDate && schedulingRequirement.endDate && <br />}
            {schedulingRequirement.endDate && (
              <span className="font-bold">
                {t('features:discounts.components.list_table.tooltips.until')}{' '}
                {formattedStartDate(schedulingRequirement.endDate)}
              </span>
            )}
            .
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          spendAmountRequirement
            ? 'features:discounts.components.list_table.tooltips.spend-required'
            : 'features:discounts.components.list_table.tooltips.spend-not-required',
        )}
        icon="calculator"
        enabled={!!spendAmountRequirement}
        inline
      >
        {spendAmountRequirement && (
          <div>
            {spendAmountRequirement.min && currencyRequirement && (
              <span className="font-bold">
                {t('features:discounts.components.list_table.tooltips.at-least')}{' '}
                {getCurrencySymbol(currencyRequirement)}
                {formatMoneyNoSymbol(spendAmountRequirement.min, currencyRequirement)}
              </span>
            )}
            {spendAmountRequirement.min && spendAmountRequirement.max && (
              <span> {t('features:discounts.components.list_table.tooltips.and')} </span>
            )}
            {spendAmountRequirement.max && currencyRequirement && (
              <span className="font-bold">
                {t('features:discounts.components.list_table.tooltips.lower-than')}{' '}
                {getCurrencySymbol(currencyRequirement)}
                {formatMoneyNoSymbol(spendAmountRequirement.max, currencyRequirement)}
              </span>
            )}
            .
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          numItemsRequirement
            ? 'features:discounts.components.list_table.tooltips.num-items-required'
            : 'features:discounts.components.list_table.tooltips.num-items-not-required',
        )}
        icon="cart"
        enabled={!!numItemsRequirement}
        inline
      >
        {numItemsRequirement && (
          <div>
            {numItemsRequirement.min && (
              <span className="font-bold">
                {t('features:discounts.components.list_table.tooltips.at-least')}{' '}
                {numItemsRequirement.min}
              </span>
            )}
            {numItemsRequirement.min && numItemsRequirement.max && (
              <span> {t('features:discounts.components.list_table.tooltips.and')} </span>
            )}
            {numItemsRequirement.max && (
              <span className="font-bold">
                {t('features:discounts.components.list_table.tooltips.lower-than')}{' '}
                {numItemsRequirement.max}
              </span>
            )}
            .
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          maxUsesRequirement
            ? 'features:discounts.components.list_table.tooltips.max-uses-required'
            : 'features:discounts.components.list_table.tooltips.max-uses-not-required',
        )}
        icon="xCircle"
        enabled={!!maxUsesRequirement}
        inline
      >
        {maxUsesRequirement && (
          <div>
            <span className="font-bold">
              {maxUsesRequirement.number}{' '}
              {t('features:discounts.components.list_table.tooltips.times')}
            </span>
            .
          </div>
        )}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          excludedCustomersRequirement
            ? 'features:discounts.components.list_table.tooltips.excluded-customers-required'
            : 'features:discounts.components.list_table.tooltips.excluded-customers-not-required',
        )}
        icon="userRemove"
        enabled={!!excludedCustomersRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={t(
          productRequirement
            ? 'features:discounts.components.list_table.tooltips.products-required'
            : 'features:discounts.components.list_table.tooltips.products-not-required',
        )}
        icon="cube"
        enabled={!!productRequirement}
      />

      <BadgeTooltipWrapper
        tooltip={t(
          simultaneityRequirement === null || simultaneityRequirement.length > 0
            ? simultaneityRequirement !== null
              ? 'features:discounts.components.list_table.tooltips.simultaneity-required'
              : 'features:discounts.components.list_table.tooltips.simultaneity-required-all'
            : 'features:discounts.components.list_table.tooltips.simultaneity-not-required',
        )}
        icon="checkCircle"
        enabled={!!(simultaneityRequirement === null || simultaneityRequirement.length > 0)}
      >
        {simultaneityRequirement === null ||
          (simultaneityRequirement.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2">
              {simultaneityRequirement.map((simultaneousDiscount) => (
                <Badge key={simultaneousDiscount.internal_name} variant="green">
                  {simultaneousDiscount.internal_name}
                </Badge>
              ))}
            </div>
          ))}
      </BadgeTooltipWrapper>

      <BadgeTooltipWrapper
        tooltip={t(
          currencyRequirement
            ? 'features:discounts.components.list_table.tooltips.currency-required'
            : 'features:discounts.components.list_table.tooltips.currency-not-required',
        )}
        icon="money"
        enabled={!!currencyRequirement}
        inline
      >
        {currencyRequirement && (
          <div>
            <span className="font-bold">
              {currencyRequirement}{' '}
              {t('features:discounts.components.list_table.tooltips.currency')}
            </span>
            .
          </div>
        )}
      </BadgeTooltipWrapper>
    </div>
  );
}
